import type { TokenRequest } from "ably"
import { Mutex } from "async-mutex"
import type {
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react"
import { createApi, retry } from "@reduxjs/toolkit/query/react"
import fetchBaseQuery from "./fetchBaseQuery"
import { clearAuth, updateTokens } from "@/auth/slice"
import type { TokenResponse } from "@/auth/model"
import { getRefreshToken } from "@/auth/refreshTokenStorage"
import { withRefreshLock } from "@/auth/refreshLock"
import { isRefreshTokenRejected } from "@/auth/refreshFailure"

export type BaseQueryExtraOptions = {
  readonly withoutAuth?: boolean
}

type Assert = (condition: boolean) => asserts condition

const getResponseCode = (
  error: FetchBaseQueryError | undefined,
): number | undefined => {
  if (!error) {
    return undefined
  }

  const status = error.status

  if (status === "PARSING_ERROR") {
    return error.originalStatus
  }

  if (typeof status != "number") {
    return undefined
  }

  return status
}

const mutex = new Mutex()

/**
 * Endpoints the refresh path must never touch: the refresh itself, or a dead token would spawn
 * refreshes of refreshes, and logout. Both run under the cross-tab refresh lock, so waiting on the
 * mutex or the lock from here would deadlock.
 */
const neverRefreshes = (endpoint: string) =>
  endpoint === "tokenRefresh" || endpoint === "logout"

/**
 * Requests the refresh path skips, so their 401 is final. One sent without credentials cannot have
 * expired ones: its 401 means what it sent was wrong, and a refresh would sign a signed-in user out.
 */
const isIsolated = (endpoint: string, extraOptions: object | undefined) =>
  neverRefreshes(endpoint) ||
  // Not BaseQueryExtraOptions: retryCondition gets these typed without the endpoint's own keys.
  (!!extraOptions &&
    "withoutAuth" in extraOptions &&
    !!extraOptions.withoutAuth)

const baseQuery = retry(
  async (
    args: string | FetchArgs,
    api,
    extraOptions?: BaseQueryExtraOptions,
  ) => {
    const { dispatch, endpoint } = api
    const isolated = isIsolated(endpoint, extraOptions)

    const baseQuery = fetchBaseQuery({
      withoutAuth: !!extraOptions?.withoutAuth,
    })

    if (!isolated) {
      // Wait for a possible concurrent token refresh
      await mutex.waitForUnlock()
    }

    const result = await baseQuery(args, api, extraOptions ?? {})

    // If another request is already refreshing, let this one fail and retry with the new token.
    // Tightly coupled with backoff and retryCondition below.
    if (
      getResponseCode(result.error) === 401 &&
      !mutex.isLocked() &&
      !isolated
    ) {
      const release = await mutex.acquire()
      try {
        const giveUp = (usedToken?: string): never => {
          dispatch(clearAuth(usedToken))
          retry.fail(result.error)
        }

        const assert: Assert = (condition) => {
          if (!condition) {
            giveUp()
          }
        }

        // The mutex only covers this tab. Read the token inside the lock, never before it.
        await withRefreshLock(async () => {
          const refreshToken = getRefreshToken()
          assert(!!refreshToken)

          try {
            await dispatch(
              baseApi.endpoints.tokenRefresh.initiate(refreshToken),
            ).unwrap()
          } catch (error) {
            // Unless rejected, keep the token: this request fails, the next one refreshes again.
            if (isRefreshTokenRejected(error)) {
              giveUp(refreshToken)
            }

            retry.fail(result.error)
          }
        })
      } finally {
        release()
      }
    }

    return result
  },
  {
    backoff: async (attempt, maxRetries) => {
      if (attempt === 1) {
        // No waiting for first retry to allow instant recovery after token refresh
        return
      }

      // Below is the defaultBackoff copied from RTK as it is not exported

      const attempts = Math.min(attempt, maxRetries) - 1

      const timeout = (Math.random() + 0.4) * (300 << attempts)
      await new Promise<void>((resolve) =>
        setTimeout(() => {
          resolve()
        }, timeout),
      )
    },
    retryCondition: (error, _args, { attempt, baseQueryApi, extraOptions }) => {
      // Of client errors, retry only a 401, which the refresh above may have fixed. Not for
      // isolated requests, which it never runs for: their 401 is final, and retrying the refresh
      // itself would replay a spent token.
      const responseCode = getResponseCode(
        error as FetchBaseQueryError | undefined,
      )

      const shouldRetry =
        !responseCode ||
        (responseCode === 401 &&
          !isIsolated(baseQueryApi.endpoint, extraOptions)) ||
        responseCode < 400 ||
        responseCode >= 500

      return shouldRetry && attempt < 3
    },
  },
)

const baseApi = createApi({
  baseQuery,
  refetchOnMountOrArgChange: 60,
  refetchOnReconnect: true,
  endpoints: (build) => ({
    tokenRefresh: build.mutation<TokenResponse, string>({
      query: (payload) => ({
        url: "security/refresh",
        method: "POST",
        body: { refreshToken: payload },
      }),
      extraOptions: { withoutAuth: true },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled

          dispatch(updateTokens(data))
        } catch {
          // noop
        }
      },
    }),
    ablyAuth: build.mutation<TokenRequest, void>({
      query: () => ({
        url: "ably-auth",
        method: "POST",
      }),
    }),
  }),
})

export type BaseQuery = typeof baseQuery

export const { useTokenRefreshMutation, useAblyAuthMutation } = baseApi

export default baseApi
